// Share flow for the composited VTO result.
//
// Reality in 2026: no platform lets third-party web apps post directly to
// Instagram feeds. We use the best-available hand-off:
//
//   1. Web Share API with files  — native share sheet, user picks IG
//      (covers Android Chrome, iOS 16+ Safari, modern Samsung Internet)
//   2. Instagram Stories deep-link  — iOS-only URL scheme that pre-loads
//      the image as a Story background
//   3. Download fallback  — user saves locally, opens Instagram manually
//
// The composited image is already created on-device; sharing is purely
// user-initiated. No image ever passes through our servers.

import type { MakeupStyle, ProductMatch } from '../catalog/catalog';
import { capitaliseBrand } from '../catalog/catalog';
import { shareBlobNative } from '../native/capacitorBridge';

export type ShareOutcome = 'shared' | 'cancelled' | 'downloaded' | 'unsupported';

function buildShareTitle(style: MakeupStyle): string {
  return `${style.name} · BeautyIQ`;
}

function buildShareText(style: MakeupStyle, activeMatch: ProductMatch | null): string {
  const productLine = activeMatch
    ? `${capitaliseBrand(activeMatch.brand)} · ${activeMatch.productName}`
    : style.tagline;
  // Deep link allo stato del look (App.svelte ripristina ?style= / ?shade=):
  // chi riceve il link apre la stessa Look Card, non solo l'immagine.
  return `${style.name} ✨  ${productLine}\nProva questo look su di te: https://beautyiq.cloud/try-on/?style=${encodeURIComponent(style.id)}`;
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function canShareFiles(files: File[]): boolean {
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  return typeof nav.canShare === 'function' && nav.canShare({ files });
}

async function tryNativeShare(
  file: File,
  style: MakeupStyle,
  activeMatch: ProductMatch | null
): Promise<ShareOutcome> {
  try {
    await navigator.share({
      files: [file],
      title: buildShareTitle(style),
      text: buildShareText(style, activeMatch)
    });
    return 'shared';
  } catch (err) {
    if ((err as Error).name === 'AbortError') return 'cancelled';
    throw err;
  }
}

// iOS Stories scheme: instagram-stories://share — opens the Stories camera.
// We can't pre-load the pasteboard from a browser, so the user taps "Add
// photo" once inside the app. Not as tight as a native SDK, but avoids
// the download-then-upload dance.
function openInstagramStories(): void {
  window.location.href = 'instagram-stories://share';
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // iOS Safari can be slow to start the download after the click — revoking
  // too early causes the save to fail silently. 10s is generous but safe;
  // the browser still releases the blob on page unload.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export interface ShareRequest {
  blob: Blob;
  style: MakeupStyle;
  activeMatch: ProductMatch | null;
}

export async function shareComposite(req: ShareRequest): Promise<ShareOutcome> {
  const { blob, style, activeMatch } = req;
  const filename = `beautyiq-${style.id}.jpg`;
  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

  // Shell nativa iOS (Capacitor): WKWebView non ha navigator.share con file né
  // download delegate → share sheet nativa via Filesystem+Share. Se i plugin
  // non ci sono (web/PWA) torna null e proseguiamo con i path web.
  const nativeOutcome = await shareBlobNative(
    blob,
    filename,
    buildShareTitle(style),
    buildShareText(style, activeMatch)
  );
  if (nativeOutcome) return nativeOutcome;

  if (canShareFiles([file])) {
    return tryNativeShare(file, style, activeMatch);
  }

  // No Web Share API with files — iOS < 16 or desktop browsers. Fall back
  // to download; if iOS, also attempt to open Stories so the user has one
  // tap less if they want to post there.
  triggerDownload(blob, filename);
  if (isIos()) {
    setTimeout(openInstagramStories, 400);
  }
  return 'downloaded';
}
